import { useCallback, useEffect, useRef } from 'react'

import arriereBackoffice from '@avantstay/arriere-clients/dist/arriereBackoffice'
import arriereOperationsAI from '@avantstay/arriere-clients/dist/arriereOperationsAI'
import arrierePublic from '@avantstay/arriere-clients/dist/arrierePublic'

import * as auth from './modules/auth'
import * as bookingAdjustment from './modules/bookingAdjustment'
import * as bookingPrices from './modules/bookingPrices'
import * as bookingReview from './modules/bookingReview'
import bookings from './modules/bookings'
import bookingsCreate from './modules/bookingsCreate'
import bookingServices from './modules/bookingServices'
import { bookingsList } from './modules/bookingsList'
import books from './modules/books'
import calendarBlocks from './modules/calendarBlocks'
import * as coupons from './modules/coupons'
import * as dashboard from './modules/dashboard'
import * as emailInvoices from './modules/emailInvoices'
import finance from './modules/finance'
import global from './modules/global'
import * as guestDamagesReport from './modules/guestDamagesReport'
import guests from './modules/guests'
import homeServices from './modules/homeServices'
import jurisdictions from './modules/jurisdictions'
import * as omniSearch from './modules/omniSearch'
import owners from './modules/owners'
import payments from './modules/payments'
import properties from './modules/properties'
import propertiesList from './modules/propertiesList'
import propertyCreation from './modules/propertyCreation'
import * as quotes from './modules/quotes'
import ratings from './modules/ratings'
import * as recurringBookingBased from './modules/recurringBookingBased'
import * as recurringCases from './modules/recurringCases'
import * as recurringExpenses from './modules/recurringExpenses'
import regions from './modules/regions'
import reports from './modules/reports'
import * as revenueAgents from './modules/revenueAgents'
import * as revenueAnalyst from './modules/revenueAnalyst'
import * as revenueTickets from './modules/revenueTickets'
import * as scheduledSms from './modules/scheduledSms'
import settings from './modules/settings'
import staffAndZones from './modules/staffAndZones'
import * as taxonomy from './modules/taxonomy'
import termsAndConditions from './modules/termsAndConditions'
import usersAndPermissions from './modules/usersAndPermissions'
import * as vendors from './modules/vendors'
import * as workOrders from './modules/workOrders'
import * as workOrderTemplates from './modules/workOrderTemplates'
import { ArriereRequest, arriereRequestListener, GetEmail } from './request-listener'
import { arriereResponseListener } from './response-listener'

/** `requiresAuth: false` for the public endpoint, which also serves signed-out requests. */
const arriereApis = [
  { api: arriereBackoffice, requiresAuth: true },
  { api: arrierePublic, requiresAuth: false },
  { api: arriereOperationsAI, requiresAuth: true },
]

/**
 * Authenticates every arriere client request through the request listener, which asks for the
 * session token per request. The clients hold no static auth headers, so a request never carries
 * a token that has since expired. A request rejected with 401 is retried once, and the listener
 * then asks Clerk for a newly minted token. Without a session token, only public requests are sent.
 */
export const useArriereHeaders = ({ getEmail }: { getEmail: GetEmail }) => {
  const getEmailRef = useRef<GetEmail | null>(null)

  const requestListener = useCallback(
    (requiresAuth: boolean) =>
      ({ headers, retry }: ArriereRequest) =>
        arriereRequestListener({
          headers,
          retry,
          getEmail: getEmailRef.current,
          requiresAuth,
        }),
    [],
  )

  useEffect(() => {
    getEmailRef.current = getEmail
  })

  useEffect(() => {
    arriereApis.forEach(({ api }) => api.setRetryConfig({ max: 0, unauthorized: true }))
    const unsubscribeListeners = arriereApis.flatMap(({ api, requiresAuth }) => [
      api.addResponseListener(arriereResponseListener),
      api.addRequestListener(requestListener(requiresAuth)),
    ])

    return () => {
      unsubscribeListeners.forEach(unsubscribe => unsubscribe())
    }
  }, [])
}

export const arriere = {
  auth,
  bookingAdjustment,
  bookingPrices,
  bookings,
  bookingsCreate,
  bookingServices,
  bookingsList,
  bookingReview,
  books,
  coupons,
  dashboard,
  emailInvoices,
  finance,
  global,
  guests,
  homeServices,
  jurisdictions,
  omniSearch,
  payments,
  properties,
  propertyCreation,
  propertiesList,
  quotes,
  regions,
  ratings,
  reports,
  revenueAgents,
  revenueAnalyst,
  revenueTickets,
  scheduledSms,
  settings,
  termsAndConditions,
  usersAndPermissions,
  workOrders,
  owners,
  workOrderTemplates,
  calendarBlocks,
  staffAndZones,
  vendors,
  recurringCases,
  recurringExpenses,
  recurringBookingBased,
  guestDamagesReport,
  taxonomy,
}
